import { Component, OnInit, ViewChild, OnDestroy, Input} from '@angular/core';

import { Observable, Subscription, BehaviorSubject } from 'rxjs';
import { tap, finalize, map } from "rxjs/operators";
import { FinancialAssuranceSummary } from '../../../../common/models/financialAssurance.model';
import { FinancialAssuranceService } from '../../../../common/services/financial-assurance.service';
import { OrganizationService } from "../../../../common/services/organization.service";
import { OrganizationComplianceDetails, FinancialComplianceSearchSpec } from "../../../../common/models/organization.model";
import { AuthService } from '../../../../services/auth.service';
import { FinancialAssuranceBondingComponent } from '../../../../common/components/financial-assurance/fa-bonding.component';
import { FinancialAssuranceInsuranceComponent } from '../../../../common/components/financial-assurance/fa-insurance.component';
import { FinancialAssurancePendingComponent } from '../../../../common/components/financial-assurance/fa-pending.component';
import { FinancialAssuranceNotSubmittedFlowComponent } from '../../../../common/components/financial-assurance/fa-not-submitted-flow.component';
import { FinancialAssuranceReturnedFlowComponent } from '../../../../common/components/financial-assurance/fa-returned-flow.component';

@Component({
    selector: 'fa-external-widget',
    templateUrl: './fa-external-widget.component.html',
    standalone: false
})

export class FinancialAssuranceExternalWidget implements OnInit, OnDestroy {
    @Input() widgetTitle: string;


    @ViewChild(FinancialAssuranceBondingComponent, { static: true }) public _bondingGrid: FinancialAssuranceBondingComponent;
    @ViewChild(FinancialAssuranceInsuranceComponent, { static: true }) public _insuranceGrid: FinancialAssuranceInsuranceComponent;
    @ViewChild(FinancialAssurancePendingComponent, { static: true }) public _pendingGrid: FinancialAssurancePendingComponent;
    @ViewChild(FinancialAssuranceNotSubmittedFlowComponent, { static: true }) public _notSubmittedGrid: FinancialAssuranceNotSubmittedFlowComponent;
    @ViewChild(FinancialAssuranceReturnedFlowComponent, { static: true }) public _returnedGrid: FinancialAssuranceReturnedFlowComponent;

    _isRecalculating: boolean = false;

    get isRecalculating(): boolean {
        return this._isRecalculating;
    }

    set isRecalculating(value: boolean) {
        this._isRecalculating = value;
    }

    loadingCompliance: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    get complianceIsLoading(): boolean {
        return this.loadingCompliance.value;
    }

    complianceDetail: OrganizationComplianceDetails;
    orgId: string;

    subscription: Subscription = new Subscription();

    constructor(public service: FinancialAssuranceService,       
        public authService: AuthService,
        private organizationService: OrganizationService) { }

    ngOnInit() {
        this.orgId = this.authService.User.organizationId;
        this.load(this.orgId);
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    onTabClicked(): void {
        this._bondingGrid.resizeColumns();
        this._insuranceGrid.resizeColumns();
        this._notSubmittedGrid.resizeColumns();
        this._pendingGrid.resizeColumns();
        this._returnedGrid.resizeColumns();
    }

    load(organization: string): void {
        let spec = {
            organizationIds: [],
            includeHistoricOwnerNumbers: false,
            organizationId: organization,
            historicOwnerNumber: "",
            exactMatch: true
        } as FinancialComplianceSearchSpec;

        let detail$ = this.organizationService.getComplianceData(spec).pipe(
            map((details: OrganizationComplianceDetails[]) => {
                if (details != null && details.length > 0) return details[0]; //should ever only be one entry for an organization by id since its used as primary key
                return null;
            }),
            tap((summary: OrganizationComplianceDetails) => {
                this.complianceDetail = summary;
            }),
            finalize(() => { this.loadingCompliance.next(false); })
        ).subscribe(
            () => { },
            (error) => { console.error(error); }
        );
        this.subscription.add(detail$);
    }

    recalculate(organization: string): void {
        if (this.isRecalculating === false) {
            this.isRecalculating = true;
            this.complianceDetail = null;
            let detail$ = this.organizationService.updateCompliance(organization).pipe(
                tap((summary: OrganizationComplianceDetails) => {
                    this.complianceDetail = summary;
                }),
                finalize(() => { this.isRecalculating = false; })
            ).subscribe(
                () => { this.isRecalculating = false; },
                (error) => { console.error(error); }
            );
            this.subscription.add(detail$);
        }        
    }
}
